import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../layout/Main/Layout';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import { DesktopDatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { Add, Delete, Edit } from '@mui/icons-material';
import { get, post, put, del, postFiles } from '../../config/axios';
import toast from 'react-hot-toast';

const Webinars = () => {
    const [webinars, setWebinars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentWebinar, setCurrentWebinar] = useState(null);
    const [newWebinar, setNewWebinar] = useState({
        title: '',
        date: null,
        startTime: null,
        endTime: null,
        hostName: '',
        hostDesignation: '',
        shortDescription: '',
        thumbnailUrl: '',
        registrationLink: '',
        buttonText: '',
        visibility: true
    });
    const [imageFile, setImageFile] = useState(null);
    const fileInputRef = useRef(null);

    // Fetch all webinars
    const fetchWebinars = async () => {
        try {
            setLoading(true);
            const response = await get('/api/dashboard/website/webinars');
            setWebinars(response);
        } catch (error) {
            toast.error('Failed to fetch webinars');
            console.error('Error fetching webinars:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWebinars();
    }, []);

    // Handle image upload
    const handleImageUpload = async (file) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await postFiles("/api/app/user/uploadImage", formData);
            return response.data.url;
        } catch (error) {
            console.error("Error uploading image:", error);
            toast.error("Failed to upload image");
            return null;
        }
    };

    const handleOpenAddDialog = () => {
        setCurrentWebinar(null);
        setNewWebinar({
            title: '',
            date: null,
            startTime: null,
            endTime: null,
            hostName: '',
            hostDesignation: '',
            shortDescription: '',
            thumbnailUrl: '',
            registrationLink: '',
            buttonText: '',
            visibility: true
        });
        setImageFile(null);
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (webinar) => {
        setCurrentWebinar(webinar);
        setNewWebinar({
            ...webinar,
            date: webinar.date ? dayjs(webinar.date) : null,
            startTime: webinar.startTime ? dayjs(webinar.startTime) : null,
            endTime: webinar.endTime ? dayjs(webinar.endTime) : null
        });
        setImageFile(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewWebinar(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date) => {
        setNewWebinar(prev => ({ ...prev, date }));
    };

    const handleTimeChange = (name, time) => {
        setNewWebinar(prev => ({ ...prev, [name]: time }));
    };

    const handleVisibilityChange = (e) => {
        setNewWebinar(prev => ({ ...prev, visibility: e.target.checked }));
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.match('image.*')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }

        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (event) => {
            setNewWebinar(prev => ({ ...prev, thumbnailUrl: event.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleSaveWebinar = async () => {
        try {
            if (!newWebinar.title || !newWebinar.hostName || !newWebinar.buttonText) {
                toast.error('Title, host name, and button text are required');
                return;
            }

            if (newWebinar?.shortDescription.length > 300) {
                toast.error('Short description should be 300 characters or less');
                return;
            }

            let thumbnailUrl = newWebinar.thumbnailUrl;

            if (imageFile) {
                const uploadedUrl = await handleImageUpload(imageFile);
                if (!uploadedUrl) return;
                thumbnailUrl = uploadedUrl;
            }

            const webinarData = {
                title: newWebinar.title,
                date: newWebinar.date ? newWebinar.date.toISOString() : null,
                startTime: newWebinar.startTime ? newWebinar.startTime.toISOString() : null,
                endTime: newWebinar.endTime ? newWebinar.endTime.toISOString() : null,
                hostName: newWebinar.hostName,
                hostDesignation: newWebinar.hostDesignation,
                shortDescription: newWebinar.shortDescription,
                thumbnailUrl,
                registrationLink: newWebinar.registrationLink,
                buttonText: newWebinar.buttonText,
                visibility: newWebinar.visibility
            };

            if (currentWebinar) {
                const response = await put(`/api/dashboard/website/webinars/${currentWebinar._id}`, webinarData);
                setWebinars(webinars?.map(webinar =>
                    webinar._id === currentWebinar._id ? response : webinar
                ));
                toast.success('Webinar updated successfully');
            } else {
                const response = await post('/api/dashboard/website/webinars', webinarData);
                setWebinars([...webinars, response]);
                toast.success('Webinar added successfully');
            }
            setOpenDialog(false);
            fetchWebinars();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
            console.error('Error saving webinar:', error);
        }
    };

    const handleDeleteWebinar = async (id) => {
        if (window.confirm('Are you sure you want to delete this webinar?')) {
            try {
                await del(`/api/dashboard/website/webinars/${id}`);
                setWebinars(webinars.filter(webinar => webinar._id !== id));
                toast.success('Webinar deleted successfully');
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to delete webinar');
                console.error('Error deleting webinar:', error);
            }
        }
    };

    if (loading) {
        return (
            <Layout>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="lg" sx={{ padding: '0 !important' }}>
                <Box sx={{ my: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5">
                            Manage Webinars
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenAddDialog}
                        >
                            Add Webinar
                        </Button>
                    </Box>

                    {webinars?.length === 0 ? (
                        <Typography variant="body1" sx={{ textAlign: 'center', mt: 30 }}>
                            No webinars found. Add your first webinar.
                        </Typography>
                    ) : (
                        <Grid container spacing={5}>
                            {webinars?.map((webinar) => (
                                <Grid item xs={12} sm={6} md={4} key={webinar._id}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardMedia
                                            component="img"
                                            image={webinar.thumbnailUrl}
                                            alt={webinar.title}
                                            sx={{ height: 200, objectFit: 'cover' }}
                                        />
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography gutterBottom variant="h6" component="div">
                                                {webinar.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {webinar.hostName}
                                                {webinar.hostDesignation && `, ${webinar.hostDesignation}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                {webinar.shortDescription}
                                            </Typography>
                                            {webinar.date && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    {dayjs(webinar.date).format('MM/DD/YYYY')}
                                                </Typography>
                                            )}
                                        </CardContent>
                                        <Box sx={{ px: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton
                                                aria-label="edit"
                                                onClick={() => handleOpenEditDialog(webinar)}
                                                sx={{ mr: 1 }}
                                            >
                                                <Edit sx={{ color: "#4285F4" }} />
                                            </IconButton>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleDeleteWebinar(webinar._id)}
                                            >
                                                <Delete sx={{ color: "red" }} />
                                            </IconButton>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* Add/Edit Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                    <DialogTitle>
                        {currentWebinar ? 'Edit Webinar' : 'Add New Webinar'}
                    </DialogTitle>
                    <DialogContent>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box
                                component="form"
                                sx={{
                                    '& .MuiTextField-root': { my: 1, width: '100%' },
                                }}
                                noValidate
                                autoComplete="off"
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            required
                                            name="title"
                                            label="Webinar Title"
                                            value={newWebinar.title}
                                            onChange={handleInputChange}
                                        />
                                        <DesktopDatePicker
                                            label="Date"
                                            value={newWebinar.date}
                                            onChange={handleDateChange}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                        <TimePicker
                                            label="Start Time"
                                            value={newWebinar.startTime}
                                            onChange={(time) => handleTimeChange('startTime', time)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                        <TimePicker
                                            label="End Time"
                                            value={newWebinar.endTime}
                                            onChange={(time) => handleTimeChange('endTime', time)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                        <TextField
                                            required
                                            name="hostName"
                                            label="Speaker/Host Name"
                                            value={newWebinar.hostName}
                                            onChange={handleInputChange}
                                        />
                                        <TextField
                                            name="hostDesignation"
                                            label="Host Designation"
                                            value={newWebinar.hostDesignation}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            name="shortDescription"
                                            label="Short Description (max 300 chars)"
                                            value={newWebinar.shortDescription}
                                            onChange={handleInputChange}
                                            multiline
                                            rows={3}
                                            inputProps={{ maxLength: 300 }}
                                        />
                                        <TextField
                                            name="registrationLink"
                                            label="Registration Link"
                                            value={newWebinar.registrationLink}
                                            onChange={handleInputChange}
                                            type="url"
                                        />
                                        <TextField
                                            required
                                            name="buttonText"
                                            label="Button Text"
                                            value={newWebinar.buttonText}
                                            onChange={handleInputChange}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={newWebinar.visibility}
                                                    onChange={handleVisibilityChange}
                                                    name="visibility"
                                                />
                                            }
                                            label="Visible on Website"
                                        />
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Thumbnail / Banner Image
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                fullWidth
                                            >
                                                {imageFile ? 'Change Image' : 'Upload Image'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={handleFileSelect}
                                                    ref={fileInputRef}
                                                />
                                            </Button>
                                            {newWebinar.thumbnailUrl && (
                                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <img
                                                        src={newWebinar.thumbnailUrl}
                                                        alt="Thumbnail Preview"
                                                        style={{ maxWidth: '100%', maxHeight: '150px', marginBottom: '8px' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {imageFile?.name || 'Current thumbnail image'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            onClick={handleSaveWebinar}
                            variant="contained"
                            disabled={!newWebinar.title || !newWebinar.hostName || !newWebinar.buttonText || (!newWebinar.thumbnailUrl && !imageFile)}
                        >
                            {currentWebinar ? 'Update' : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Layout>
    );
};

export default Webinars;